import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { Router, RouterOutlet, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, ResolveStart, ResolveEnd, RouteConfigLoadStart, RouteConfigLoadEnd } from '@angular/router';
import { NavComponent } from './shared/components/nav/nav.component';
import { fromEvent, merge, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FooterComponent } from './shared/components/footer/footer.component';
import { CommonModule, DOCUMENT } from '@angular/common';
import { I18nService } from './shared/services/i18n.service';
import { trigger, transition, style, animate, query, group, animateChild } from '@angular/animations';
import { debounceTime, map, scan, startWith, distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, NavComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('routeAnimations', [
      transition('* <=> *', [
        // Prepare stacking context
        style({ position: 'relative' }),
        query(':enter, :leave', [
          style({ position: 'absolute', left: 0, right: 0, width: '100%' }),
        ], { optional: true }),
        // Run leave and enter together for a cross-fade
        group([
          query(':leave', [
            animate('120ms ease-out', style({ opacity: 0, transform: 'translateY(4px)' })),
          ], { optional: true }),
          query(':enter', [
            style({ opacity: 0, transform: 'translateY(6px)' }),
            animate('240ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
          ], { optional: true }),
        ]),
        // Allow child animations if any
        query(':enter', animateChild(), { optional: true }),
      ]),
    ]),
    trigger('overlayFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('120ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('180ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit {
  private readonly locale = inject(LOCALE_ID);
  private readonly translateService = inject(TranslateService);
  private readonly i18nService = inject(I18nService);
  private readonly DOCUMENT = inject(DOCUMENT);
  private readonly router = inject(Router);

  protected scrollPercentage = 0;
  protected loading$!: Observable<boolean>;

  ngOnInit(): void {
    this.handleUpdateAppLanguage();
    this.DOCUMENT.documentElement.lang = this.i18nService.getLanguage();

    // Track navigation + resolver + lazy-load states, debounced to avoid flicker
    const start$ = this.router.events.pipe(
      filter(
        e =>
          e instanceof NavigationStart ||
          e instanceof ResolveStart ||
          e instanceof RouteConfigLoadStart,
      ),
      map(() => 1),
    );

    const end$ = this.router.events.pipe(
      filter(
        e =>
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError ||
          e instanceof ResolveEnd ||
          e instanceof RouteConfigLoadEnd,
      ),
      map(() => -1),
    );

    this.loading$ = merge(start$, end$).pipe(
      // Sum increments/decrements, keep non-negative
      scan((count, delta) => Math.max(0, count + delta), 0),
      map(count => count > 0),
      startWith(false),
      debounceTime(150),
      distinctUntilChanged(),
    );
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    const scrollOffset =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const windowHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;
    this.scrollPercentage = (scrollOffset / windowHeight) * 100;
  }

  private handleUpdateAppLanguage(): void {
    // Reload window whenever the user's preferred language changes
    fromEvent(window, 'languagechange').subscribe(() =>
      window.location.reload(),
    );

    // Set the app language at startup
    this.translateService.use(this.locale);
  }

  // Provide a changing value to trigger route animations
  protected prepareRoute(outlet: RouterOutlet) {
    // Use only route data to avoid accessing RouterOutlet.activatedRoute (can trigger NG04012)
    return outlet?.activatedRouteData?.['animation'] ?? '';
  }
}
